import classNames from 'classnames';
import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Input from './Input';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';

import './DatePicker.css';
import { format } from 'date-fns';
import { CalendarDaysIcon, ClockIcon } from 'lucide-react';
import { getCurrentCopenhagenTime } from 'utils/dateTime';

interface DateTimePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  className?: string;
  inputClassName?: string;
  error?: string | boolean;
  value: Date | undefined;
  label?: string | React.ReactElement;
  onChange?: (newDate: Date | undefined, name: string) => void;
}

const dateFormatter = Intl.DateTimeFormat('en', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});

function DateTimePicker({
  className,
  value,
  onChange,
  onBlur,
  name,
  ...rest
}: DateTimePickerProps) {
  const defaultClassNames = getDefaultClassNames();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // Reference for DayPicker container
  const datePickerInputRef = useRef<HTMLLabelElement>(null); // Reference for DayPicker input

  const [selectedDate, setSelectedDateFn] = useState(value);
  const [selectedMonth, setSelectedMonth] = useState(value ?? new Date());
  const [selectedTime, setSelectedTime] = useState<string>(getCurrentCopenhagenTime());

  const handleDayPickerSelect = (newDate: Date | undefined, newTime?: string) => {
    setSelectedDateFn(newDate);
    if (onChange) {
      const formattedNewDate = newDate
        ? new Date(format(newDate, 'yyyy-MM-dd') + `T${newTime ?? selectedTime}Z`)
        : undefined;
      onChange(formattedNewDate, name ?? '');
    }
  };

  const handleTimeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newTime = e.target.value;
    setSelectedTime(newTime);
    if (selectedDate) {
      handleDayPickerSelect(new Date(format(selectedDate, 'yyyy-MM-dd') + `T${newTime}Z`), newTime);
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        datePickerInputRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        !datePickerInputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the date picker if click is outside
        if (onBlur) {
          onBlur(new Event('blur') as unknown as React.FocusEvent<HTMLInputElement>);
        }
        // setSelectedDate(new Date(rest.value));
      }
    },
    [onBlur]
  );

  // Handle clicking outside of the date picker to close it
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const formattedValue = useMemo(() => {
    if (!value) {
      return '';
    }

    const v: string = value
      ? // @ts-expect-error typescript isn't up to date with date formatRange
        dateFormatter.formatRange(value, value)
      : '';
    return v;
  }, [value]);

  return (
    <div className={classNames('relative', className)} data-component="DateTimePicker">
      <Input
        labelIcon={
          <span className="text-heading inline-block">
            <CalendarDaysIcon size={16} />
          </span>
        }
        onFocus={() => {
          setIsOpen(true);
        }}
        autoComplete="off"
        labelRef={datePickerInputRef}
        value={formattedValue}
        onChange={() => {
          if (onChange) {
            handleDayPickerSelect(undefined, '00:00');
          }
        }}
        readOnly
        {...rest}
      />
      {isOpen && (
        <div
          ref={datePickerRef}
          className="absolute z-10 rounded-md border-2 border-neutral-500 bg-white px-3"
        >
          <DayPicker
            timeZone="UTC"
            captionLayout="dropdown"
            mode="single"
            selected={selectedDate}
            onMonthChange={(newMonth) => {
              setSelectedMonth(newMonth);
            }}
            fixedWeeks
            weekStartsOn={1}
            onSelect={(newDate: Date | undefined) => handleDayPickerSelect(newDate, selectedTime)}
            showOutsideDays
            required={false}
            month={selectedMonth}
            classNames={{
              day: 'rounded-md',
              today: `rounded-md bg-blue-50 underline`, // Add a border to today's date
              selected: `bg-blue-600 text-white`, // Highlight the selected day
              range_middle: `bg-blue-100`, // Highlight the range of selected days
              // root: `${defaultClassNames.root} shadow-lg p-5`, // Add a shadow to the root element
              chevron: `fill-blue-600`, // Change the color of the chevron
              // month: 'text-heading',
              button_previous: 'text-green-800 fill-red-800',
              caption_label: `${defaultClassNames.caption_label} text-neutral-800`,
            }}
          />
          <div className="mt-1 mb-2 flex items-center justify-center gap-2 border-t border-neutral-200 pt-3">
            <ClockIcon size={16} className="text-neutral-800" />
            <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
              Time
              <input
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                className="h-9 rounded border border-neutral-500 bg-white px-2 font-normal text-neutral-800 outline-offset-1 outline-none focus:outline-4 focus:outline-blue-200"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default DateTimePicker;
